/**
 * API Service
 * Handles communication with Webhook Server
 */

import type {
  SearchCriteria,
  RoomAvailability,
  BookingRequest,
  BookingConfirmation,
} from './types';
import { getCurrentLanguage, setCustomAmenities } from './i18n';
import { getTenantId as getConfigTenantId, getHotelCode as getConfigHotelCode, setRuntimeHotelCode } from './config';

/**
 * Get API Base URL - MUST be called at runtime, not module load time
 */
function getApiBaseUrl(): string {
  // Check environment variable first
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl) {
    console.log('[API] Using VITE_API_BASE_URL:', envUrl);
    return envUrl;
  }

  // Server-side rendering fallback
  if (typeof window === 'undefined' || !window.location) {
    console.log('[API] SSR fallback: localhost:4001');
    return 'http://localhost:4001';
  }

  const { protocol, hostname } = window.location;
  console.log('[API] Runtime hostname:', hostname);

  // Map frontend hostnames to API hostname (webhook-server)
  const mapping: Record<string, string> = {
    'mini-app.metagoldtech.com': 'webhook-server.metagoldtech.com',
    'lineoa-hotel-prod.web.app': 'webhook-server.metagoldtech.com',
    'hotel-admin.metagoldtech.com': 'webhook-server.metagoldtech.com',
    'hotel-app.metagoldtech.com': 'webhook-server.metagoldtech.com',
    'hotel.metagoldtech.com': 'webhook-server.metagoldtech.com',
  };

  if (mapping[hostname]) {
    const url = `${protocol}//${mapping[hostname]}`;
    console.log('[API] Mapped to:', url);
    return url;
  }

  if (hostname === 'localhost') {
    console.log('[API] Localhost detected, using port 4001');
    return 'http://localhost:4001';
  }

  // Default to webhook-server for unknown production hostnames
  const url = `${protocol}//webhook-server.metagoldtech.com`;
  console.log('[API] Default to webhook-server:', url);
  return url;
}

function getTenantId(): string {
  return getConfigTenantId();
}

// Hotel info type
export interface HotelInfo {
  hotelCode?: string;
  hotelName: string;
  hotelAddress: string;
  hotelRating: number;
  hotelImage: string;
  hotelPhone: string;
  hotelEmail: string;
  checkInTime: string;
  checkOutTime: string;
  maxAdults: number;
  maxChildren: number;
  maxRooms: number;
  supportedLanguages: string[];
  features: Record<string, boolean>;
  customAmenities?: Array<{
    code: string;
    icon: string;
    name: Record<string, string>;
  }>;
  miniAppCacheTtlSeconds?: number;
}

// Dynamic Cache TTL (default 2 minutes, updated dynamically from Admin settings)
let dynamicCacheTtlMs = 2 * 60 * 1000;

export function setDynamicCacheTtlSeconds(seconds?: number): void {
  if (typeof seconds === 'number' && seconds >= 0) {
    dynamicCacheTtlMs = seconds * 1000;
  }
}

// Cache for hotel info (per language)
const hotelInfoCache = new Map<string, { data: HotelInfo; timestamp: number }>();

/**
 * Get hotel info from API (cached)
 */
export async function getHotelInfo(): Promise<HotelInfo | null> {
  const tenantId = getTenantId();
  const lang = getCurrentLanguage();
  const cacheKey = `${tenantId}:${lang}`;

  // Check cache
  const cached = hotelInfoCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < dynamicCacheTtlMs) {
    if (cached.data.hotelCode) {
      setRuntimeHotelCode(cached.data.hotelCode);
    }
    if (cached.data.customAmenities) {
      setCustomAmenities(cached.data.customAmenities);
    }
    setDynamicCacheTtlSeconds(cached.data.miniAppCacheTtlSeconds);
    return cached.data;
  }

  try {
    const result = await apiFetch<{ success: boolean; data: HotelInfo }>(
      `/api/${tenantId}/hotel-info?lang=${lang}`
    );

    if (result.success && result.data) {
      if (result.data.hotelCode) {
        setRuntimeHotelCode(result.data.hotelCode);
      }
      setDynamicCacheTtlSeconds(result.data.miniAppCacheTtlSeconds);
      hotelInfoCache.set(cacheKey, { data: result.data, timestamp: Date.now() });
      if (result.data.customAmenities) {
        setCustomAmenities(result.data.customAmenities);
      }
      return result.data;
    }
    return null;
  } catch (error) {
    console.error('[API] Get hotel info error:', error);
    return null;
  }
}

/**
 * Generic API fetch with error handling
 */
export async function apiFetch<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  // Get API base URL dynamically each time - MUST NOT cache this value
  const API_BASE = getApiBaseUrl();
  const TENANT_ID = getTenantId();
  const url = `${API_BASE}${endpoint}`;

  console.log('[API] Fetching:', url, '| Tenant:', TENANT_ID);

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || `API error: ${response.status}`);
  }

  return response.json();
}

// API response format from webhook server
interface ApiRoomStay {
  roomTypeCode: string;
  roomTypeName: string;
  roomDescription?: string;
  maxOccupancy: number;
  maxAdults: number;
  maxChildren: number;
  extraAdultCharge: number;
  extraChildCharge: number;
  amenities: string[];
  ratePlanCode: string;
  ratePlanName: string;
  rateDescription?: string;
  dailyRates: Array<{
    date: string;
    amountBeforeTax: number;
    amountAfterTax: number;
  }>;
  totalBeforeTax: number;
  totalAfterTax: number;
  currency: string;
  availability: number;
  isRefundable?: boolean;
}

/**
 * Search for available rooms
 */
export async function searchRooms(criteria: SearchCriteria): Promise<RoomAvailability[]> {
  const params = new URLSearchParams({
    checkIn: criteria.checkIn,
    checkOut: criteria.checkOut,
    adults: criteria.adults.toString(),
    children: criteria.children.toString(),
    rooms: criteria.rooms.toString(),
    language: getCurrentLanguage(),
  });

  const tenantId = getTenantId();
  const result = await apiFetch<{
    success: boolean;
    data: {
      hotelCode: string;
      hotelName: string;
      roomStays: ApiRoomStay[];
    };
  }>(`/api/${tenantId}/availability?${params}`);

  if (!result.success || !result.data) {
    throw new Error('Failed to search rooms');
  }

  // Calculate nights
  const checkInDate = new Date(criteria.checkIn);
  const checkOutDate = new Date(criteria.checkOut);
  const nights = Math.ceil(
    (checkOutDate.getTime() - checkInDate.getTime()) / (1000 * 60 * 60 * 24)
  );

  // Transform API response to Mini App format
  const hotelCode = result.data.hotelCode;
  const hotelName = result.data.hotelName;

  // Number of rooms requested
  const numberOfRooms = criteria.rooms;

  return (result.data.roomStays || []).map((room: ApiRoomStay): RoomAvailability => {
    // Calculate rate per single room from daily rates if totalAfterTax is 0
    let ratePerRoom = room.totalAfterTax;
    if (ratePerRoom === 0 && room.dailyRates?.length > 0) {
      ratePerRoom = room.dailyRates.reduce((sum, day) => sum + day.amountAfterTax, 0);
    }

    // Total rate = ratePerRoom × numberOfRooms (AfterTax — actual amount to pay)
    const totalRate = ratePerRoom * numberOfRooms;

    // Calculate average nightly rate BEFORE tax (for display with ++ notation)
    let rateBeforeTax = room.totalBeforeTax;
    if (rateBeforeTax === 0 && room.dailyRates?.length > 0) {
      rateBeforeTax = room.dailyRates.reduce((sum, day) => sum + day.amountBeforeTax, 0);
    }
    const averageNightlyRate = nights > 0 ? Math.round(rateBeforeTax / nights) : rateBeforeTax;

    return {
      roomTypeCode: room.roomTypeCode,
      roomTypeName: room.roomTypeName,
      ratePlanCode: room.ratePlanCode,
      ratePlanName: room.ratePlanName,
      totalRate,
      ratePerRoom,
      averageNightlyRate,
      totalBeforeTax: rateBeforeTax,
      numberOfRooms,
      currency: room.currency,
      available: room.availability,
      description: room.roomDescription,
      bedType: (room as any).bedType || '',
      bedTypeDesc: (room as any).bedTypeDesc || '',
      mealPlan: room.rateDescription,
      maxAdults: room.maxAdults,
      maxChildren: room.maxChildren,
      extraAdultCharge: room.extraAdultCharge,
      extraChildCharge: room.extraChildCharge,
      amenities: room.amenities || [],
      isRefundable: room.isRefundable,
      // Add hotel info so UI can fetch images
      hotelCode,
      hotelName,
    };
  });
}

/**
 * Create a booking
 */
export async function createBooking(booking: BookingRequest): Promise<BookingConfirmation> {
  const tenantId = getTenantId();
  const result = await apiFetch<{
    success: boolean;
    data: BookingConfirmation;
  }>(`/api/${tenantId}/booking`, {
    method: 'POST',
    body: JSON.stringify(booking),
  });

  if (!result.success) {
    throw new Error('Failed to create booking');
  }

  return result.data;
}

/**
 * Update booking payment status (e.g. pay_at_hotel, pending, paid)
 */
export async function updatePaymentStatus(
  confirmationNumber: string,
  paymentStatus: 'pending' | 'paid' | 'failed' | 'pay_at_hotel'
): Promise<void> {
  const tenantId = getTenantId();
  await apiFetch<{ success: boolean }>(
    `/api/${tenantId}/booking/${confirmationNumber}/payment`,
    { method: 'PATCH', body: JSON.stringify({ paymentStatus }) }
  );
}

/**
 * Get payment method display string (e.g. "Visa ****1234", "PromptPay")
 */
export async function getPaymentMethodDisplay(paymentIntentId: string): Promise<string | null> {
  try {
    const tenantId = getTenantId();
    const result = await apiFetch<{
      success: boolean;
      data: { type: string; brand?: string; last4?: string };
    }>(`/api/${tenantId}/payment-method/${paymentIntentId}`);
    if (!result.success || !result.data) return null;
    const { type, brand, last4 } = result.data;
    if (type === 'promptpay') return 'PromptPay';
    if (type === 'card' && brand && last4) {
      const brandName = brand.charAt(0).toUpperCase() + brand.slice(1);
      return `${brandName} ****${last4}`;
    }
    if (type === 'card' && last4) return `Card ****${last4}`;
    return null;
  } catch {
    return null;
  }
}

/**
 * Get booking details by confirmation number
 */
export async function getBooking(confirmationNumber: string): Promise<BookingConfirmation | null> {
  try {
    const tenantId = getTenantId();
    const result = await apiFetch<{
      success: boolean;
      data: BookingConfirmation;
    }>(`/api/${tenantId}/booking/${confirmationNumber}`);

    return result.success ? result.data : null;
  } catch {
    return null;
  }
}

// In-memory cache for room images (cleared on page reload)
const imageCache = new Map<
  string,
  Array<{ path: string; url: string; thumbUrl?: string; order?: number }>
>();

/**
 * Get room images metadata for a room type
 */
export async function getRoomImages(
  tenantId: string,
  hotelCode: string,
  roomTypeCode: string
): Promise<Array<{ path: string; url: string; thumbUrl?: string; order?: number }>> {
  let activeHotelCode = hotelCode || getConfigHotelCode();
  if (!activeHotelCode) {
    const info = await getHotelInfo();
    if (info?.hotelCode) {
      activeHotelCode = info.hotelCode;
      setRuntimeHotelCode(activeHotelCode);
    }
  }
  if (!activeHotelCode) return [];

  // Check cache first
  const cacheKey = `${tenantId}:${activeHotelCode}:${roomTypeCode}`;
  if (imageCache.has(cacheKey)) {
    return imageCache.get(cacheKey)!;
  }

  const API_BASE = getApiBaseUrl();
  const result = await apiFetch<{
    success: boolean;
    data: Array<{
      path: string;
      url: string;
      thumbUrl?: string;
      order?: number;
      proxyUrl?: string;
    }>;
  }>(`/api/${tenantId}/hotels/${activeHotelCode}/rooms/${roomTypeCode}/images`);
  if (!result.success) return [];

  // Check if running in local dev (localhost) - only use proxy on localhost
  const isLocalDev = API_BASE.includes('localhost') || API_BASE.includes('127.0.0.1');

  // Only use proxy URL on local dev (emulator), use direct Firebase Storage URLs in production
  const images = result.data.map(img => {
    if (isLocalDev && img.proxyUrl) {
      const fullProxyUrl = `${API_BASE}${img.proxyUrl}`;
      return {
        path: img.path,
        url: fullProxyUrl,
        thumbUrl: `${fullProxyUrl}&thumb=1`,
        order: img.order,
      };
    }
    // Production: use direct Firebase Storage URLs
    return {
      path: img.path,
      url: img.url,
      thumbUrl: img.thumbUrl || img.url,
      order: img.order,
    };
  });

  // Store in cache
  imageCache.set(cacheKey, images);
  return images;
}

// ===========================================
// Room Types API (for browsing without dates)
// ===========================================

export interface RoomTypeInfo {
  roomTypeCode: string;
  name: string;
  description: string;
  maxOccupancy: number;
  maxAdults: number;
  maxChildren: number;
  amenities: string[];
  bedType: string;
  bedTypeDesc?: string;
  baseRate: number;
  currency: string;
}

export interface RoomTypesResponse {
  hotelCode: string;
  hotelName: string;
  roomTypes: RoomTypeInfo[];
}

// In-memory cache for room types
const roomTypesCache = new Map<string, { data: RoomTypesResponse; timestamp: number }>();

/**
 * Get all room types for a hotel (for browsing without dates)
 */
export async function getRoomTypes(hotelCode?: string): Promise<RoomTypesResponse | null> {
  const tenantId = getTenantId();
  let activeHotelCode = hotelCode || getConfigHotelCode();

  if (!activeHotelCode) {
    const info = await getHotelInfo();
    if (info?.hotelCode) {
      activeHotelCode = info.hotelCode;
      setRuntimeHotelCode(activeHotelCode);
    }
  }

  if (!activeHotelCode) {
    console.error('[API] Get room types error: missing hotelCode');
    return null;
  }

  const lang = getCurrentLanguage();
  const cacheKey = `${tenantId}:${activeHotelCode}:${lang}`;

  // Check cache
  const cached = roomTypesCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < dynamicCacheTtlMs) {
    return cached.data;
  }

  try {
    const result = await apiFetch<{ success: boolean; data: RoomTypesResponse }>(
      `/api/${tenantId}/hotels/${activeHotelCode}/room-types?language=${lang}`
    );

    if (result.success && result.data) {
      roomTypesCache.set(cacheKey, { data: result.data, timestamp: Date.now() });
      return result.data;
    }
    return null;
  } catch (error) {
    console.error('[API] Get room types error:', error);
    return null;
  }
}

/**
 * Health check
 */
export async function healthCheck(): Promise<boolean> {
  try {
    const result = await apiFetch<{ status: string }>('/health');
    return result.status === 'healthy';
  } catch {
    return false;
  }
}

// ===========================================
// User Profile API (for auto-fill guest info)
// ===========================================

export interface UserGuestProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  title?: string;
  nationality?: string;
  idType?: string;
  idNumber?: string;
  updatedAt?: string;
}

/**
 * Get user profile for auto-fill
 */
export async function getUserProfile(lineUserId: string): Promise<UserGuestProfile | null> {
  const tenantId = getTenantId();

  try {
    console.log('[API] Getting user profile for:', lineUserId);
    const result = await apiFetch<{ success: boolean; data: UserGuestProfile | null }>(
      `/api/${tenantId}/user/${lineUserId}/profile`
    );

    if (result.success && result.data) {
      console.log('[API] User profile loaded:', result.data);
      return result.data;
    }
    console.log('[API] No user profile found');
    return null;
  } catch (error) {
    console.error('[API] Get user profile error:', error);
    return null;
  }
}

/**
 * Save user profile for auto-fill next time
 */
export async function saveUserProfile(
  lineUserId: string,
  profile: UserGuestProfile
): Promise<boolean> {
  const tenantId = getTenantId();

  try {
    console.log('[API] Saving user profile for:', lineUserId);
    const result = await apiFetch<{ success: boolean }>(
      `/api/${tenantId}/user/${lineUserId}/profile`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profile),
      }
    );

    if (result.success) {
      console.log('[API] User profile saved successfully');
      return true;
    }
    return false;
  } catch (error) {
    console.error('[API] Save user profile error:', error);
    return false;
  }
}

// ===========================================
// Payment API (Stripe)
// ===========================================

/**
 * Get Stripe publishable key from backend
 */
export async function getPaymentConfig(): Promise<{ provider?: string; publishableKey?: string; merchantId?: string } | null> {
  try {
    const tenantId = getTenantId();
    const result = await apiFetch<{
      success: boolean;
      data: { provider?: string; publishableKey?: string; merchantId?: string };
    }>(`/api/${tenantId}/payment/config`);
    return result.success ? result.data : null;
  } catch (error) {
    console.error('[API] Get payment config error:', error);
    return null;
  }
}

/**
 * Create a PaymentIntent for a booking
 */
export async function createPaymentIntent(params: {
  confirmationNumber: string;
  amount: number;
  currency: string;
  customerEmail: string;
  customerName: string;
}): Promise<{ clientSecret: string; paymentIntentId: string; redirectUrl?: string } | null> {
  try {
    const tenantId = getTenantId();
    const result = await apiFetch<{
      success: boolean;
      data: { clientSecret: string; paymentIntentId: string; amount: number; currency: string; redirectUrl?: string };
    }>(`/api/${tenantId}/payment/create-intent`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
    return result.success ? result.data : null;
  } catch (error) {
    console.error('[API] Create payment intent error:', error);
    return null;
  }
}

/**
 * Get payment status
 */
export async function getPaymentStatus(
  paymentIntentId: string
): Promise<{ status: string; receiptUrl?: string } | null> {
  try {
    const tenantId = getTenantId();
    const result = await apiFetch<{
      success: boolean;
      data: { status: string; receiptUrl?: string };
    }>(`/api/${tenantId}/payment/${paymentIntentId}/status`);
    return result.success ? result.data : null;
  } catch (error) {
    console.error('[API] Get payment status error:', error);
    return null;
  }
}

/**
 * Get dynamic Spa Service config for current tenant
 */
export async function getSpaConfig(): Promise<{
  enabled: boolean;
  openTime: string;
  closeTime: string;
  slotIntervalMinutes: number;
  maxBedsPerSlot: number;
  treatments: Array<{ code: string; nameTh: string; nameEn?: string; duration: number; price: number; desc?: string }>;
} | null> {
  try {
    const tenantId = getTenantId();
    const result = await apiFetch<{
      success: boolean;
      data: any;
    }>(`/api/${tenantId}/services/spa`);
    return result.success ? result.data : null;
  } catch (error) {
    console.error('[API] Get spa config error:', error);
    return null;
  }
}

/**
 * Get dynamic Restaurant Service config for current tenant
 */
export async function getRestaurantConfig(): Promise<{
  enabled: boolean;
  restaurantName: string;
  mealPeriods: Array<{ id: string; name: string; timeSlots: string[]; maxPaxPerSlot: number }>;
} | null> {
  try {
    const tenantId = getTenantId();
    const result = await apiFetch<{
      success: boolean;
      data: any;
    }>(`/api/${tenantId}/services/restaurant`);
    return result.success ? result.data : null;
  } catch (error) {
    console.error('[API] Get restaurant config error:', error);
    return null;
  }
}

export interface ActiveServicesState {
  rooms: boolean;
  spa: boolean;
  restaurant: boolean;
}

let cachedActiveServices: ActiveServicesState = { rooms: true, spa: true, restaurant: true };

export async function fetchActiveServicesConfig(): Promise<ActiveServicesState> {
  try {
    const [hotelInfo, spaConfig, restConfig] = await Promise.all([
      getHotelInfo().catch(() => null),
      getSpaConfig().catch(() => null),
      getRestaurantConfig().catch(() => null),
    ]);

    const hasRooms = Boolean(hotelInfo && hotelInfo.features?.roomBooking !== false);
    
    // Check Spa: Must have valid id or non-empty active outlets list
    const spaOutlets = (spaConfig as any)?.outlets;
    const hasSpa = Boolean(
      spaConfig && 
      spaConfig.enabled !== false && 
      ((Array.isArray(spaOutlets) && spaOutlets.length > 0 && spaOutlets.some((o: any) => o.enabled !== false)) ||
       ((spaConfig as any).id && (spaConfig as any).enabled !== false))
    );

    // Check Restaurant: Must have valid id or non-empty active outlets list
    const restOutlets = (restConfig as any)?.outlets;
    const hasRestaurant = Boolean(
      restConfig && 
      restConfig.enabled !== false && 
      ((Array.isArray(restOutlets) && restOutlets.length > 0 && restOutlets.some((o: any) => o.enabled !== false)) ||
       ((restConfig as any).id && (restConfig as any).enabled !== false))
    );

    cachedActiveServices = { rooms: hasRooms, spa: hasSpa, restaurant: hasRestaurant };
    return cachedActiveServices;
  } catch (err) {
    console.error('[API] Error checking active services:', err);
    return cachedActiveServices;
  }
}

export function getActiveServices(): ActiveServicesState {
  return cachedActiveServices;
}
